import React, { useState, useEffect, useRef } from "react";
import ReactTooltip from "react-tooltip";
import PopupMenu from "../../SimpleForm/PopupMenu";
import DatePicker from "react-datepicker";
// import dateFnsFormat from "date-fns/format";
// import { IoIosCalendar } from "react-icons/io";
// import addDays from "date-fns/addDays";
import CurrencyInput from "react-currency-input-field";
import { FiAlertTriangle } from "react-icons/fi";
import { FaEllipsisH } from "react-icons/fa";
import { useRecoilValue } from "recoil";
import { validator } from "../../SimpleForm/validation";
import { useSetTableErrors } from "../../SimpleForm/Atoms/Table/hooks";
import { tableRowsErrorsSelector } from "../../SimpleForm/Atoms/Table/selectors";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.scss";
import moment from "moment";

/**
 *
 *  RenderDateInput Error Validation logic is not robust and needs to be abstracted and modularized.
 *
 */
const getCellData = (prop) => {
  const columnId = prop.column.id;
  const rowIndex = prop.row.index;
  const machineName = prop.machineName;

  return {
    columnId: prop.column.id,
    columnHeader: prop.column.Header,
    rowIndex: prop.row.index,
    rowData: prop.data[prop.row.index],
    toolTipKey: `${rowIndex}_${columnId}`,
    rowName: `${machineName}[${rowIndex}]`,
    fieldName: `${machineName}[${rowIndex}][${columnId}]`,
    fieldArray: prop.fieldArray,
    machineName: prop.machineName,
    machineNameArray: prop.machineNameArray,
    value: prop.fieldArray.fields.value[`${columnId}`],
    charLimit: prop.column.charLimit || null,
    input: prop.column.input || null,
    prependValue: prop.column.prependValue || null,
    formApi: prop.formApi,
    formData: prop.formData,
    blsCounties: prop.formData._blsCounties
      ? JSON.parse(prop.formData._blsCounties)
      : [],
    validate: prop.column.validate || null,
    requiredFields: prop.column.requiredFields || [],
  };
};

const renderTooltip = (value, toolTipKey) => {
  if (value && value.label) {
    value = value.label;
  }
  return value ? (
    <ReactTooltip type="dark" effect="solid" id={`${toolTipKey}`}>
      <span>{value}</span>
    </ReactTooltip>
  ) : null;
};

const isRequiredFieldsSet = (requiredFields, formData) => {
  let messages = [];
  if (!requiredFields && !formData) {
    return messages;
  }
  requiredFields.forEach((item) => {
    if (!formData[item.field]) {
      messages.push(item.message);
    }
  });
  return messages;
};

const getErrorBorder = (error) => {
  return error
    ? { border: "1px dashed red" }
    : { border: "0px", borderBottom: "1px dotted black" };
};

const dateValidationCheck = ({
  beginOrEnd,
  beginDate,
  endDate,
  rowBeginDate,
  rowEndDate,
  setError,
  errSection,
}) => {
  // this is because internal has a time of 12 hrs but frontend has 0 hours... we subtract
  // beginDate = beginDate - 43200000;
  // endDate = endDate - 43200000;
  const opposite = beginOrEnd === "begin" ? "end" : "begin";
  const noData = `You must fill out section ${errSection}.`;
  const selectText = "Select a date that is ";
  const less = "less than or equal to the ";
  const greater = "greater than or equal to the ";
  const lessThanErrRow = selectText + less + `${opposite} date in this row.`;
  const lessThanErrSection =
    selectText + less + `${opposite} date in section ${errSection}.`;
  const greaterThanErrSection =
    selectText + greater + `${beginOrEnd} date in section ${errSection}.`;

  if (!beginOrEnd) {
    return setError(`An error occurred with date.`);
  }

  if (
    (!beginDate && beginOrEnd === "begin") ||
    (!endDate && beginOrEnd === "end")
  ) {
    return setError(noData);
  }

  if (beginOrEnd === "begin" && beginDate) {
    if (rowEndDate && rowBeginDate > rowEndDate) {
      return setError(lessThanErrRow);
    }
    if (endDate && rowBeginDate > endDate) {
      return setError(lessThanErrSection);
    }
    if (rowBeginDate < beginDate) {
      return setError(greaterThanErrSection);
    }
    return setError(false);
  }

  if (beginOrEnd === "end" && endDate) {
    if (rowBeginDate && rowBeginDate > rowEndDate) {
      return setError(lessThanErrRow);
    }
    if (beginDate && rowEndDate < beginDate) {
      return setError(lessThanErrSection);
    }
    if (rowEndDate > endDate) {
      return setError(greaterThanErrSection);
    }
    return setError(false);
  }
};

/**
 * This function is used to check if the new wage selected is lower than the originally determined wage.
 */
const validateWage = (rowVal, rowPer, rowDVal, rowDPer) => {
  let rowDeterminedValue;
  let rowValue;

  /**
   * These are the originally determined wages from internal hence D/Determined.
   * Base conversion to hourly of originally determined wage to save space.
   * These string comparisons are based off DH.
   *
   * These updated integer divisions are based off originally stipulated values from client.
   * They differ from LCA and PERM.
   */
  if (rowDPer === "Hourly") rowDeterminedValue = parseFloat(rowDVal);
  if (rowDPer === "Weekly") rowDeterminedValue = parseFloat(rowDVal / 35);
  if (rowDPer === "Biweekly") rowDeterminedValue = parseFloat(rowDVal / 70);
  if (rowDPer === "Monthly") rowDeterminedValue = parseFloat(rowDVal / 140);
  if (rowDPer === "Annual") rowDeterminedValue = parseFloat(rowDVal / 1820);
  /**
   * These are the new user-entered values that must be equal or higher than the internal values.
   * Base conversion to hourly of new wage to save space.
   * These string comparisons are based off front end dropdown.
   *
   * These updated integer divisions are based off originally stipulated values from client.
   * They differ from LCA and PERM.
   */
  if (rowPer === "Hourly") rowValue = parseFloat(rowVal);
  if (rowPer === "Weekly") rowValue = parseFloat(rowVal / 35);
  if (rowPer === "Biweekly") rowValue = parseFloat(rowVal / 70);
  if (rowPer === "Monthly") rowValue = parseFloat(rowVal / 140);
  if (rowPer === "Annual") rowValue = parseFloat(rowVal / 1820);
  if (rowPer === "Piece Rate") {
    // do nothing for now bc piece rate is variable
  }

  return rowValue < rowDeterminedValue
    ? `The 'from' rate must be greater than or equal to ${rowDVal} per ${rowDPer}, the prevailing wage for this worksite`
    : null;
};

const RenderWarning = (prop) => {
  let rowsWithErrors = useRecoilValue(tableRowsErrorsSelector);
  const { rowIndex, machineName } = getCellData(prop);
  let check = rowsWithErrors[`${machineName}`] || [];
  let rowHasError = check.includes(`${rowIndex}`);
  const errorIcon = rowHasError ? (
    <FiAlertTriangle
      style={{
        style: "light",
        marginLeft: "-32px",
        position: "absolute",
        color: "red",
      }}
      fill={"white"}
      size={"1em"}
    />
  ) : null;
  return (
    <>
      {errorIcon} {rowIndex + 1}
    </>
  );
};

const RenderActions = (prop) => {
  const { setDeletedRow } = prop;
  const { rowIndex, rowData, machineNameArray } = getCellData(prop);

  const contentStyle = {
    boxShadow: "inset 0px 4px 0px 0px #AEB0B5, 0 2px 6px 0 rgba(0,0,0,0.5)",
    padding: "0",
    paddingTop: "4px",
    border: "none",
  };
  const arrowStyle = {
    width: "0",
    height: "0",
    borderStyle: "solid",
    borderWidth: "0 0 13px 13px",
    outline: "none",
    borderColor: "transparent transparent #AEB0B5 transparent",
    background: "transparent",
    boxShadow: "none",
  };
  return (
    <div style={{ display: "grid", placeItems: "center" }}>
      <PopupMenu
        contentStyle={contentStyle}
        arrowStyle={arrowStyle}
        trigger={
          <button
            type="button"
            className="menu-item menu-item-button dropdown-label-View"
          >
            <FaEllipsisH
              className="EllipsisIcon"
              size={20}
              style={{ cursor: "pointer" }}
            />
          </button>
        }
        position="bottom right"
        closeOnDocumentClick
      >
        <div className="Memenunu">
          <button
            className="menu-item menu-item-button dropdown-label-View"
            style={{ cursor: "pointer" }}
            type="button"
            onClick={() => {
              const newRowData = {
                ...rowData,
                rowNum: new Date().getTime().toString(),
              };
              machineNameArray.fields.insert(rowIndex + 1, newRowData);
            }}
          >
            <span style={{ color: "#0071BB" }}>Copy</span>
          </button>
          <hr />
          <div className="menu">
            <button
              className="menu-item menu-item-button dropdown-label-View"
              style={{ cursor: "pointer" }}
              type="button"
              onClick={() => {
                machineNameArray.fields.remove(rowIndex);
                setDeletedRow(rowIndex);
              }}
            >
              <span style={{ color: "#9C1712" }}>Remove</span>
            </button>
          </div>
        </div>
      </PopupMenu>
    </div>
  );
};

const RenderInput = (prop) => {
  const {
    rowIndex,
    columnId,
    columnHeader,
    toolTipKey,
    fieldName,
    value,
    machineName,
    formApi,
    formData,
    charLimit,
    input,
    validate,
    requiredFields,
  } = getCellData(prop);
  const [val, setVal] = useState(value);
  const [error, setError] = useState(false);

  // React Drag and Drop changes value, need to update when that happens
  useEffect(() => {
    setVal(value);
  }, [value]);

  const handleChange = (e) => {
    const fieldsRequired = isRequiredFieldsSet(requiredFields, formData);
    if (fieldsRequired && fieldsRequired.length) {
      e.preventDefault();
      // setError(fieldsRequired);
      setVal(null);
      return;
    }

    let errorMessage = null;
    let targetValue = e.target.value;
    if (input?.type === "number" && e.target.value) {
      targetValue = Math.abs(parseInt(targetValue)).toString();
    }
    if (validate && formData) {
      errorMessage = validator(validate, formData)(targetValue);
    }
    setVal(targetValue);
    formApi.change(`${fieldName}`, targetValue);
    setError(errorMessage);
  };

  useEffect(() => {
    const fieldsRequired = isRequiredFieldsSet(requiredFields, formData);
    if (fieldsRequired && fieldsRequired.length) {
      // setError(fieldsRequired);
      setVal(null);
      return;
    }
    const errorMessage = validator(validate, formData)(value);
    setError(errorMessage);
  }, [rowIndex, value]);
  useSetTableErrors(val, error, machineName, rowIndex, columnId);
  const border = getErrorBorder(error);

  return (
    <>
      <input
        name={`${toolTipKey}`}
        {...input}
        maxLength={charLimit}
        title={columnHeader}
        value={val || ""}
        style={{ ...border, width: "100%" }}
        data-tip
        data-for={`${toolTipKey}`}
        onChange={handleChange}
        onBlur={handleChange}
      />
      {renderTooltip(error || val, toolTipKey)}
    </>
  );
};

const RenderDateInput = ({
  beginDateProperty,
  endDateProperty,
  rowBeginDateProperty,
  rowEndDateProperty,
  originalDateSection,
  ...props
}) => {
  const {
    rowIndex,
    columnId,
    toolTipKey,
    fieldName,
    value,
    machineName,
    formApi,
    formData,
  } = getCellData(props);
  let rowBeginDate,
    rowEndDate,
    formBeginDate,
    formEndDate,
    initialMonth,
    appxBeginDate,
    appxEndDate;

  const dateRef = useRef();
  const [error, setError] = useState(false);
  const [day, setDay] = useState(value ? new Date(value) : null);

  formBeginDate = formData[beginDateProperty]
    ? formData[beginDateProperty]
    : null;
  formEndDate = formData[endDateProperty] ? formData[endDateProperty] : null;
  appxBeginDate =
    props.fieldArray?.fields?.value?.[rowBeginDateProperty] || null;
  appxEndDate = props.fieldArray?.fields?.value?.[rowEndDateProperty] || null;
  const beginDate = new Date(formBeginDate).getTime();
  const endDate = new Date(formEndDate).getTime();

  if (columnId === rowBeginDateProperty) {
    rowBeginDate = formBeginDate;
    rowEndDate = appxEndDate || formEndDate || null;
    initialMonth = appxBeginDate || formBeginDate || null;
  }
  if (columnId === rowEndDateProperty) {
    rowEndDate = formEndDate;
    rowBeginDate = appxBeginDate || formBeginDate || null;
    initialMonth = appxEndDate || formEndDate || null;
  }
  if (new Date(rowBeginDate) > new Date(rowEndDate)) {
    rowEndDate = formEndDate;
  }
  if (new Date(rowEndDate) < new Date(rowBeginDate)) {
    rowBeginDate = formBeginDate;
  }
  if (new Date(rowBeginDate) < new Date(formBeginDate)) {
    rowBeginDate = formBeginDate;
  }
  if (new Date(rowEndDate) > new Date(formEndDate)) {
    rowEndDate = formEndDate;
  }

  const validateDate = () => {
    if (day) {
      dateValidationCheck({
        beginOrEnd: "begin",
        beginDate,
        endDate,
        rowBeginDate: day.getTime(),
        rowEndDate: new Date(appxEndDate).getTime(),
        setError,
        errSection: originalDateSection,
      });
    }
    if (day) {
      dateValidationCheck({
        beginOrEnd: "end",
        beginDate,
        endDate,
        rowBeginDate: new Date(appxBeginDate).getTime(),
        rowEndDate: day.getTime(),
        setError,
        errSection: originalDateSection,
      });
    }
  };

  const handleChange = (day) => {
    if (!day) {
      formApi.change(`${fieldName}`, null);
      setDay(null);
      setError(false);
    } else {
      const alreadyCorrected = day.toString().includes("12:00:00");
      const dateCorrectedToNoon = new Date(moment(day).add(12, "h"));
      setDay(alreadyCorrected ? day : dateCorrectedToNoon);
      setError(false);
      formApi.change(
        `${fieldName}`,
        alreadyCorrected ? day : dateCorrectedToNoon
      );
    }
  };

  useEffect(() => {
    setDay(value ? new Date(value) : null);
  }, [appxBeginDate, appxEndDate, value]);

  useEffect(() => {
    validateDate();
  }, [appxBeginDate, appxEndDate, value, day]);

  useSetTableErrors(day, error, machineName, rowIndex, columnId);
  const tooltipVal = error || "";

  const minMaxProps = {};
  if (!!rowBeginDate) {
    minMaxProps["minDate"] = new Date(rowBeginDate);
  }
  if (!!rowEndDate) {
    minMaxProps["maxDate"] = new Date(rowEndDate);
  }

  return (
    <>
      <div data-tip data-for={`${toolTipKey}`} style={{ position: "relative" }}>
        <DatePicker
          name={toolTipKey}
          ref={dateRef}
          selected={day}
          placeholderText="mm/dd/yyyy"
          dateFormat="MM/dd/yyyy"
          popperProps={{
            strategy: "fixed",
          }}
          onChange={handleChange}
          className={`${error && "error-border"}`}
          {...minMaxProps}
          // Not worth the time right now
          // customInput={
          //   <div style={{ position: "relative" }}>
          //     <input
          //       name={toolTipKey}
          //       type="text"
          //       style={{ width: "100%", height: "auto", ...border }}
          //       value={day ? dateFnsFormat(day?.getTime(), "MM/dd/yyyy") : null}
          //     />
          //     {/* <IoIosCalendar
          //       style={{
          //         right: "0px",
          //         position: "absolute",
          //         opacity: ".5",
          //       }}
          //     /> */}
          //   </div>
          // }
        />
      </div>
      {renderTooltip(tooltipVal, toolTipKey)}
    </>
  );
};
const RenderWageInput = ({
  newWagePerProperty,
  newWageFromProperty,
  newWageToProperty,
  ...props
}) => {
  const {
    rowIndex,
    rowData,
    columnId,
    toolTipKey,
    fieldName,
    value,
    machineName,
    formApi,
  } = getCellData(props);
  const [error, setError] = useState(false);
  const [val, setVal] = useState(value);

  const rowPer = rowData[newWagePerProperty];
  const rowFromVal = rowData[newWageFromProperty] || 0;
  const rowToVal = rowData[newWageToProperty] || 0;
  const rowDeterminedWage = rowData["determined_wage"];
  const rowDeterminedWagePer = rowData["determined_wage_per"];

  let handleChange = (newVal) => {
    setVal(newVal);
    validateErrors(newVal);
    formApi.change(`${fieldName}`, newVal);
  };

  const validateErrors = (newVal, firstRender) => {
    if (newVal === val && !firstRender) return;
    let errorMessage = null;

    if (newVal) {
      errorMessage = validateWage(
        newVal,
        rowPer,
        rowDeterminedWage,
        rowDeterminedWagePer
      );
    }
    if (newVal) {
      if (
        newVal !== null &&
        newVal !== "0" &&
        parseFloat(newVal) < parseFloat(rowFromVal)
      ) {
        errorMessage = "The 'to' rate must be greater than the 'from' rate";
      }
    }
    setError(errorMessage);
  };

  // React Drag and Drop changes value, need to update when that happens
  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    validateErrors(value, true);
  }, [value]);

  useSetTableErrors(val, error, machineName, rowIndex, columnId);
  const border = getErrorBorder(error);
  const tooltipVal = error || "";

  return (
    <>
      <div data-tip data-for={`${toolTipKey}`} style={{ position: "relative" }}>
        <CurrencyInput
          style={{ ...border, width: "100%" }}
          id="input-example"
          name={`${toolTipKey}`}
          placeholder="00.00"
          defaultValue={val}
          prefix="$"
          decimalsLimit={2}
          value={val}
          onValueChange={handleChange}
        />
      </div>
      {renderTooltip(tooltipVal, toolTipKey)}
    </>
  );
};

const RenderStateSelect = ({
  rowBlsAreaProperty,
  appxCountyProperty,
  ...props
}) => {
  const {
    rowIndex,
    rowData,
    columnId,
    columnHeader,
    toolTipKey,
    rowName,
    fieldName,
    value,
    machineName,
    formApi,
    blsCounties,
  } = getCellData(props);
  const rowBlsArea = rowData[rowBlsAreaProperty];
  const rowStates = blsCounties[rowBlsArea];
  const [val, setVal] = useState(value || null);
  const [error, setError] = useState(false);

  // React Drag and Drop changes value, need to update when that happens
  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    setError(!value);
    !value ? setError("This field is required.") : setError(null);
  }, []);

  const handleChange = (e) => {
    let targetValue = e.target.value;
    setVal(targetValue);
    formApi.change(`${fieldName}`, targetValue);
    formApi.change(`${rowName}[${appxCountyProperty}]`, null);
    !targetValue || targetValue === "- Select -"
      ? setError("This field is required.")
      : setError(null);
  };

  useSetTableErrors(val, error, machineName, rowIndex, columnId);
  const border = getErrorBorder(error);
  return (
    <>
      <select
        name={`${toolTipKey}`}
        value={val}
        title={columnHeader}
        style={{ ...border, width: "100%" }}
        data-tip
        data-for={`${toolTipKey}`}
        onChange={handleChange}
        onBlur={handleChange}
        disabled={Object.keys(rowStates).length < 2} //"true"
      >
        <option>- Select -</option>
        {blsCounties &&
          Object.keys(rowStates).map((state) => {
            return (
              <option key={state} value={state} selected={state === val}>
                {state}
              </option>
            );
          })}
      </select>
      {renderTooltip(error || val, toolTipKey)}
    </>
  );
};

const RenderCountySelect = ({
  rowBlsAreaProperty,
  appxStateProperty,
  ...props
}) => {
  const {
    rowIndex,
    rowData,
    columnId,
    columnHeader,
    toolTipKey,
    fieldName,
    value,
    machineName,
    formApi,
    blsCounties,
  } = getCellData(props);
  const rowState = rowData[appxStateProperty];
  const rowBlsArea = rowData[rowBlsAreaProperty];
  const rowCounties = blsCounties[rowBlsArea][rowState];
  const [val, setVal] = useState(value && value.label ? value.label : null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(!value);
    setVal(value && value.label ? value.label : null);
    !value ? setError("This field is required.") : setError(null);
  }, [rowState, rowCounties]);

  const handleChange = (e) => {
    let targetValue = e.target.value;
    let formApiValue =
      !["- Select -", null, undefined].includes(rowState) && rowCounties
        ? rowCounties.find((county) => county.label === targetValue)
        : null;
    setVal(targetValue);
    formApi.change(`${fieldName}`, formApiValue);
    !targetValue || targetValue === "- Select -"
      ? setError("This field is required.")
      : setError(null);
  };

  useSetTableErrors(val, error, machineName, rowIndex, columnId);
  const border = getErrorBorder(error);
  return (
    <>
      <select
        name={`${toolTipKey}`}
        value={val}
        title={columnHeader}
        style={{ ...border, width: "100%" }}
        data-tip
        data-for={`${toolTipKey}`}
        onChange={handleChange}
        onBlur={handleChange}
      >
        <option>- Select -</option>
        {rowCounties &&
          rowCounties.map((countyObject) => {
            return (
              <option
                key={countyObject.label}
                value={countyObject.label}
                selected={countyObject.label === val}
              >
                {countyObject.label}
              </option>
            );
          })}
      </select>
      {renderTooltip(error || val, toolTipKey)}
    </>
  );
};

const RenderWageFrequency = ({ wageFrequencyArray, ...props }) => {
  const {
    rowIndex,
    columnId,
    columnHeader,
    toolTipKey,
    fieldName,
    value,
    machineName,
    formApi,
  } = getCellData(props);
  let copyValue = value;

  const [val, setVal] = useState(copyValue);
  const [error, setError] = useState(false);

  // React Drag and Drop changes value, need to update when that happens
  useEffect(() => {
    setVal(value);
  }, [value]);

  const handleChange = (e) => {
    const targetValue = e.target.value;
    setVal(targetValue);
    formApi.change(`${fieldName}`, targetValue);
  };

  // on change if error then update form errors
  useSetTableErrors(val, error, machineName, rowIndex, columnId);

  const border = getErrorBorder(error);
  return (
    <>
      <select
        name={`${toolTipKey}`}
        value={val}
        title={columnHeader}
        style={{ ...border, width: "100%" }}
        data-tip
        data-for={`${toolTipKey}`}
        onChange={handleChange}
        onBlur={handleChange}
      >
        <option hidden>- Select -</option>
        {wageFrequencyArray.map((per) => {
          return (
            <option key={per} value={per}>
              {per}
            </option>
          );
        })}
      </select>
      {renderTooltip(error || val, toolTipKey)}
    </>
  );
};

const RenderText = (prop) => {
  let { value, toolTipKey, prependValue } = getCellData(prop);
  value = value && prependValue ? `${prependValue}${value}` : value;
  value = value ? `${value}` : `--`;
  return (
    <>
      <div name={`${toolTipKey}`} data-tip data-for={`${toolTipKey}`}>
        {value}
      </div>
      {renderTooltip(value, toolTipKey)}
    </>
  );
};

export {
  getCellData,
  getErrorBorder,
  RenderWarning,
  RenderActions,
  RenderInput,
  RenderText,
  RenderDateInput,
  RenderStateSelect,
  RenderCountySelect,
  RenderWageInput,
  RenderWageFrequency,
  renderTooltip,
};
